import { useState, useEffect } from 'react'
import { apiClient } from '../services/api'
import type { UserPermissionsResponse } from '../types/api'
import type { DestMasterResponse, SubDestMasterResponse } from '../types/master'

interface UserPermissionManagerProps {
  userId: number
  userName: string
  onClose: () => void
}

export default function UserPermissionManager({
  userId,
  userName,
  onClose,
}: UserPermissionManagerProps) {
  const [userPerms, setUserPerms] = useState<UserPermissionsResponse | null>(null)
  const [destinations, setDestinations] = useState<DestMasterResponse[]>([])
  const [subDests, setSubDests] = useState<SubDestMasterResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [selectedDest, setSelectedDest] = useState<number | ''>('')
  const [selectedSubDests, setSelectedSubDests] = useState<number[]>([])
  const [isAdding, setIsAdding] = useState(false)

  useEffect(() => {
    fetchPermissions()
    fetchDestinations()
  }, [userId])

  const fetchPermissions = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiClient.getUserPermissions(userId)
      setUserPerms(response)
    } catch (err: any) {
      console.error('Failed to fetch permissions:', err)
      setError('権限情報の取得に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchDestinations = async () => {
    try {
      const data = await apiClient.getDestinations({ limit: 1000 })
      setDestinations(data)
    } catch (err) {
      console.error('Failed to fetch destinations:', err)
      setError('運用先一覧の取得に失敗しました')
    }
  }

  const fetchSubDestinations = async (destId: number) => {
    try {
      const data = await apiClient.getSubDestinations({ dest_id: destId, limit: 1000 })
      setSubDests(data)
    } catch (err) {
      console.error('Failed to fetch sub-destinations:', err)
      setError('利用先一覧の取得に失敗しました')
    }
  }

  const handleDestChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === '') {
      setSelectedDest('')
      setSelectedSubDests([])
      setSubDests([])
      return
    }
    const destId = parseInt(value, 10)
    setSelectedDest(destId)
    setSelectedSubDests([])
    fetchSubDestinations(destId)
  }

  const handleSubDestToggle = (subDestId: number) => {
    setSelectedSubDests(prev => {
      if (prev.includes(subDestId)) {
        return prev.filter(id => id !== subDestId)
      }
      return [...prev, subDestId]
    })
  }

  const handleSelectAllSubDests = () => {
    if (selectedSubDests.length === subDests.length) {
      setSelectedSubDests([])
    } else {
      setSelectedSubDests(subDests.map(sd => sd.id))
    }
  }

  const handleAddPermission = async () => {
    if (!selectedDest) {
      setError('運用先を選択してください')
      return
    }

    try {
      setIsAdding(true)
      setError(null)

      // 利用先が選択されていない場合は運用先全体へのアクセス権限を追加
      if (selectedSubDests.length === 0) {
        await apiClient.addUserPermission(userId, {
          dest_id: selectedDest,
          sub_dest_id: undefined,
        })
      } else {
        // 選択された各利用先に対して権限を追加
        const results = await Promise.allSettled(
          selectedSubDests.map(subDestId =>
            apiClient.addUserPermission(userId, {
              dest_id: selectedDest,
              sub_dest_id: subDestId,
            })
          )
        )

        // 結果を確認
        const failures = results.filter(r => r.status === 'rejected')
        const conflicts = failures.filter(
          r => r.status === 'rejected' && (r.reason as any)?.status === 409
        )

        if (failures.length > 0 && failures.length === conflicts.length) {
          // 全て重複エラーの場合
          setError('選択された権限は既に追加されています')
        } else if (failures.length > 0) {
          // 一部失敗
          const successCount = results.length - failures.length
          setError(`${successCount}件追加成功、${failures.length}件失敗しました`)
        }
      }

      // Refresh permissions
      await fetchPermissions()

      // Reset form
      setSelectedDest('')
      setSelectedSubDests([])
      setSubDests([])
    } catch (err: any) {
      console.error('Failed to add permission:', err)
      if (err.status === 409) {
        setError('この権限は既に追加されています')
      } else {
        setError('権限の追加に失敗しました')
      }
    } finally {
      setIsAdding(false)
    }
  }

  const handleDeletePermission = async (permissionId: number) => {
    if (!window.confirm('この権限を削除してもよろしいですか？')) {
      return
    }

    try {
      setError(null)
      await apiClient.deleteUserPermission(userId, permissionId)
      await fetchPermissions()
    } catch (err: any) {
      console.error('Failed to delete permission:', err)
      setError('権限の削除に失敗しました')
    }
  }

  if (isLoading) {
    return (
      <div className="modal-body text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">読み込み中...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="modal-header">
        <h5 className="modal-title">{userName} の権限管理</h5>
        <button type="button" className="btn-close" onClick={onClose} />
      </div>

      <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-circle me-2" />
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        {/* Add Permission Form */}
        <div className="card mb-4">
          <div className="card-header bg-light">
            <h6 className="mb-0">権限を追加</h6>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label htmlFor="destSelect" className="form-label">
                運用先 <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                id="destSelect"
                value={selectedDest}
                onChange={handleDestChange}
                disabled={isAdding}
              >
                <option value="">-- 選択してください --</option>
                {destinations.map(dest => (
                  <option key={dest.id} value={dest.id}>
                    {dest.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedDest && subDests.length > 0 && (
              <div className="mb-3">
                <label className="form-label d-flex align-items-center justify-content-between">
                  <span>利用先 (複数選択可)</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleSelectAllSubDests}
                    disabled={isAdding}
                  >
                    {selectedSubDests.length === subDests.length ? '全選択解除' : '全選択'}
                  </button>
                </label>
                <div
                  className="border rounded p-2"
                  style={{ maxHeight: '200px', overflowY: 'auto' }}
                >
                  {subDests.map(subDest => (
                    <div key={subDest.id} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`subDest-${subDest.id}`}
                        checked={selectedSubDests.includes(subDest.id)}
                        onChange={() => handleSubDestToggle(subDest.id)}
                        disabled={isAdding}
                      />
                      <label className="form-check-label" htmlFor={`subDest-${subDest.id}`}>
                        {subDest.name}
                      </label>
                    </div>
                  ))}
                </div>
                <small className="d-block text-muted mt-1">
                  利用先を選択しない場合、この運用先配下の全利用先にアクセスできます。
                  {selectedSubDests.length > 0 && (
                    <span className="ms-2 badge bg-primary">{selectedSubDests.length}件選択中</span>
                  )}
                </small>
              </div>
            )}

            <button
              className="btn btn-success"
              onClick={handleAddPermission}
              disabled={!selectedDest || isAdding}
            >
              {isAdding && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
              )}
              追加
            </button>
          </div>
        </div>

        {/* Current Permissions */}
        <div className="card">
          <div className="card-header bg-light">
            <h6 className="mb-0">現在の権限 ({userPerms?.permissions.length || 0}件)</h6>
          </div>
          <div className="card-body">
            {!userPerms?.permissions || userPerms.permissions.length === 0 ? (
              <div className="alert alert-info mb-0">
                <i className="fas fa-info-circle me-2" />
                権限がまだ設定されていません
              </div>
            ) : (
              <div className="list-group list-group-flush">
                {userPerms.permissions.map(perm => (
                  <div
                    key={perm.id}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="fw-bold">{perm.dest_name}</div>
                      {perm.sub_dest_name && (
                        <div className="text-muted small">
                          <i className="fas fa-arrow-right me-1" />
                          {perm.sub_dest_name}
                        </div>
                      )}
                      <div className="text-muted small">
                        追加日: {new Date(perm.created_at).toLocaleString('ja-JP')}
                      </div>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDeletePermission(perm.id)}
                      title="削除"
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          閉じる
        </button>
      </div>
    </>
  )
}
